import { Timestamp } from 'firebase/firestore';
import { activityFromJson, Activity } from '../../domain/models';
import { trainingBenefitFromMap, TrainingBenefit } from '../../domain/models';
import { breathworkFromJson, Breathwork } from '../../domain/models';
import { publicBreathworkFromJson, PublicBreathwork } from '../../domain/models';
import { feelingFromJson, Feeling } from '../../domain/models';
import { breathworkWorkoutFromJson, BreathworkWorkout } from '../../domain/models';
import { achievementFromJson, Achievement } from '../../domain/models';
import { userAchievementFromJson, UserAchievement } from '../../domain/models';
import { journeyFromJson, Journey } from '../../domain/models';
import { userJourneyFromJson, UserJourney } from '../../domain/models';
import { publicJourneyFromJson, PublicJourney } from '../../domain/models';

export function timestampToDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return undefined;
}

type FirestoreDoc = Record<string, unknown>;

export function activityFromFirestore(doc: FirestoreDoc): Activity {
  return activityFromJson(doc);
}

export function benefitFromFirestore(doc: FirestoreDoc): TrainingBenefit {
  return trainingBenefitFromMap(doc);
}

export function breathworkFromFirestore(doc: FirestoreDoc): Breathwork {
  return breathworkFromJson(doc);
}

export function publicBreathworkFromFirestore(doc: FirestoreDoc): PublicBreathwork {
  return publicBreathworkFromJson(doc);
}

export function feelingFromFirestore(doc: FirestoreDoc): Feeling {
  return feelingFromJson(doc);
}

export function workoutFromFirestore(doc: FirestoreDoc): BreathworkWorkout {
  return breathworkWorkoutFromJson({
    ...doc,
    startedAt: timestampToDate(doc['startedAt']),
    endedAt: timestampToDate(doc['endedAt']),
  });
}

export function achievementFromFirestore(doc: FirestoreDoc): Achievement {
  return achievementFromJson(doc);
}

export function userAchievementFromFirestore(doc: FirestoreDoc): UserAchievement {
  return userAchievementFromJson({
    ...doc,
    obtainedAt: ((doc['obtainedAt'] as unknown[]) ?? []).map((e) => timestampToDate(e) ?? new Date(e as string)),
  });
}

export function workoutToFirestore(workout: BreathworkWorkout): Record<string, unknown> {
  return {
    breathworkId: workout.breathworkId,
    startedAt: Timestamp.fromDate(workout.startedAt),
    endedAt: Timestamp.fromDate(workout.endedAt),
    totalDuration: workout.totalDurationSec,
    rounds: workout.rounds,
    ...(workout.breathsOnFirstRound != null && { breathsOnFirstRound: workout.breathsOnFirstRound }),
    ...(workout.incrementBetweenRounds != null && { incrementBetweenRounds: workout.incrementBetweenRounds }),
    ...(workout.inhaleDurationSec != null && { inhaleDuration: workout.inhaleDurationSec }),
    ...(workout.fullLungsRetentionDurationSec != null && { fullLungsRetentionDuration: workout.fullLungsRetentionDurationSec }),
    ...(workout.exhaleDurationSec != null && { exhaleDuration: workout.exhaleDurationSec }),
    ...(workout.emptyLungsRetentionDurationSec != null && { emptyLungsRetentionDuration: workout.emptyLungsRetentionDurationSec }),
    ...(workout.timezoneOffsetFromUtc != null && { timezoneOffsetFromUtc: workout.timezoneOffsetFromUtc }),
    ...(workout.priorFeelingsIds != null && { priorFeelingsIds: workout.priorFeelingsIds }),
    ...(workout.postFeelingsIds != null && { postFeelingsIds: workout.postFeelingsIds }),
    ...(workout.journeyStepSessionId != null && { journeyStepSessionId: workout.journeyStepSessionId }),
    ...(workout.journeyId != null && { journeyId: workout.journeyId }),
    ...(workout.journeyName != null && { journeyName: workout.journeyName }),
    ...(workout.journeyStepTitle != null && { journeyStepTitle: workout.journeyStepTitle }),
    ...(workout.journeyStepIndex != null && { journeyStepIndex: workout.journeyStepIndex }),
  };
}

export function publicJourneyFromFirestore(doc: FirestoreDoc): PublicJourney {
  return publicJourneyFromJson(doc);
}

// --- Journey parsers ---

export function journeyFromFirestore(doc: FirestoreDoc): Journey {
  return journeyFromJson(doc);
}

export function userJourneyFromFirestore(doc: FirestoreDoc): UserJourney {
  return userJourneyFromJson({
    ...doc,
    startedAt: timestampToDate(doc['startedAt']) ?? new Date(),
    lastPracticedAt: timestampToDate(doc['lastPracticedAt']) ?? null,
    completedAt: timestampToDate(doc['completedAt']) ?? null,
    completedSteps: ((doc['completedSteps'] as Record<string, unknown>[] | undefined) ?? []).map(
      (s) => ({ ...s, completedAt: timestampToDate(s['completedAt']) ?? new Date() }),
    ),
  });
}

export function userJourneyToFirestore(uj: UserJourney): Record<string, unknown> {
  return {
    userId: uj.userId,
    sourceJourneyId: uj.sourceJourneyId,
    journey: uj.journey,   // Journey is a plain object, no Timestamps inside
    status: uj.status,
    completedSteps: uj.completedSteps.map((s) => ({
      stepId: s.stepId,
      completedAt: Timestamp.fromDate(s.completedAt),
      ...(s.workoutId != null && { workoutId: s.workoutId }),
    })),
    currentStepIndex: uj.currentStepIndex,
    startedAt: Timestamp.fromDate(uj.startedAt),
    ...(uj.lastPracticedAt != null && { lastPracticedAt: Timestamp.fromDate(uj.lastPracticedAt) }),
    ...(uj.completedAt != null && { completedAt: Timestamp.fromDate(uj.completedAt) }),
  };
}
